import useFormTableFields from '../../../../shared/data/useFormTableFields';
import { listAccountingCustomFields } from '../../services/accounting-custom-fields.service';
import { invoiceFormSchema } from '../../config/accountingForm.schemas';
import React, { useEffect, useMemo, useState } from 'react';
import DataTable from '../../../../shared/components/patterns/DataTable';
import ConfirmActionDialog from '../../../../shared/components/patterns/ConfirmActionDialog';
import AccountingAttachmentsPanel from '../shared/AccountingAttachmentsPanel.jsx';
import IncomeDetailDialog from './IncomeDetailDialog.jsx';
import { AccountingInlineActions, getInvoicesTableColumns } from '../shared/accountingPage.config';

export default function InvoicesTable({
  invoices,
  payments = [],
  onUpdatePayment,
  onUpdateInvoice,
  onRegisterPayment,
  onVoidInvoice,
  canManageAccountingDestructiveActions = false,
  focusedEntityId = '',
  focusedMode = 'view',
  onClearFocus,
  views = [],
  activeViewId,
  onViewChange,
}) {
  const tableFields = useFormTableFields(invoiceFormSchema.id, listAccountingCustomFields);
  const [detailInvoice, setDetailInvoice] = useState(null);
  const currentDetailInvoice =
    invoices.find((item) => String(item.id) === String(detailInvoice?.id)) || detailInvoice;
  const [detailMode, setDetailMode] = useState('view');
  const [registerRequest, setRegisterRequest] = useState(null);
  const paymentsByInvoiceId = useMemo(() => {
    const grouped = new Map();
    payments.forEach((payment) => {
      if (payment.invoiceId === null || payment.invoiceId === undefined) return;
      const key = String(payment.invoiceId);
      grouped.set(key, [...(grouped.get(key) || []), payment]);
    });
    return grouped;
  }, [payments]);
  useEffect(() => {
    if (!focusedEntityId) return;
    const target = invoices.find((item) => String(item.id) === String(focusedEntityId));
    if (target) {
      setDetailInvoice(target);
      setDetailMode(focusedMode);
    }
  }, [focusedEntityId, invoices]);

  function getInvoicePayments(invoice) {
    return [...(paymentsByInvoiceId.get(String(invoice.id)) || [])].sort((left, right) =>
      String(left.dueDate || '').localeCompare(String(right.dueDate || ''))
    );
  }

  function getPaymentSummary(invoice, linkedPayments) {
    const total = linkedPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    const received = linkedPayments
      .filter((payment) => payment.status === 'incassato')
      .reduce((sum, payment) => sum + (payment.amount || 0), 0);
    const nextPayment = linkedPayments.find(
      (payment) =>
        payment.status !== 'incassato' && payment.status !== 'stornato' && payment.status !== 'annullato'
    );

    return {
      countLabel: `${linkedPayments.length} ${linkedPayments.length === 1 ? 'incasso' : 'incassi'}`,
      statusLabel:
        linkedPayments.length > 0 && linkedPayments.every((payment) => payment.status === 'incassato')
          ? 'Incassata'
          : linkedPayments.some((payment) => payment.status === 'in_ritardo')
            ? 'In ritardo'
            : linkedPayments.length > 0
              ? 'Da incassare'
              : 'Nessun incasso',
      nextDueDate: nextPayment?.dueDate || '',
      received,
      residual: Math.max((invoice.totalAmount || total) - received, 0),
    };
  }

  function getActions(invoice) {
    const linkedPayments = getInvoicePayments(invoice);
    const actions = [
      {
        id: 'preview',
        label: 'Anteprima fattura',
        iconKey: 'actions.preview',
        onClick: () => {
          setDetailMode('view');
          setDetailInvoice(invoice);
        },
      },
    ];
    actions.push({
      id: 'edit',
      label: 'Modifica',
      title: 'Modifica fattura',
      iconKey: 'actions.edit',
      onClick: () => {
        setDetailInvoice(invoice);
        setDetailMode('edit');
      },
    });

    if (canManageAccountingDestructiveActions && invoice.status !== 'annullata') {
      actions.push({
        id: 'void',
        label: 'Annulla',
        title: 'Annulla fattura',
        iconKey: 'actions.reject',
        tone: 'danger',
        onClick: () => onVoidInvoice?.(invoice.id),
      });
    }

    return actions;
  }

  return (
    <>
      <section className="accounting-table-section">
        {!views.length ? (
          <header className="accounting-section-heading">
            <h2>Registro Fatture</h2>
            <p>{invoices.length} fatture trovate</p>
          </header>
        ) : null}
        <DataTable
          ariaLabel="Registro Fatture"
          formSchema={invoiceFormSchema}
          views={views}
          activeViewId={activeViewId}
          onViewChange={onViewChange}
          customFields={tableFields.definitions}
          error={tableFields.error}
          columns={[
            ...getInvoicesTableColumns({ getInvoicePayments, getPaymentSummary }),
            {
              id: 'actions',
              header: '',
              render: (invoice) => (
                <AccountingInlineActions
                  actions={getActions(invoice)}
                  extra={
                    <AccountingAttachmentsPanel
                      entity={invoice}
                      linkedEntityType="ACCOUNTING_INVOICE"
                      entityLabel={invoice.invoiceNumber || `fattura ${invoice.id}`}
                      clientName={invoice.client}
                    />
                  }
                />
              ),
            },
          ]}
          rows={invoices}
          getRowId={(invoice) => invoice.id}
          onRowClick={(item) => {
            setDetailInvoice(item);
            setDetailMode('view');
          }}
          emptyState="Nessuna fattura trovata"
        />
      </section>
      <IncomeDetailDialog
        open={Boolean(detailInvoice)}
        mode="invoice"
        invoice={currentDetailInvoice}
        linkedPayments={detailInvoice ? getInvoicePayments(detailInvoice) : []}
        onClose={() => {
          setDetailInvoice(null);
          onClearFocus?.();
        }}
        focusComments={focusedMode === 'comments'}
        initialMode={detailMode}
        onRegisterPayment={(payment) => payment && setRegisterRequest(payment)}
        onUpdatePayment={onUpdatePayment}
        onUpdateInvoice={onUpdateInvoice}
      />
      <ConfirmActionDialog
        open={Boolean(registerRequest)}
        title="Registrare questo incasso?"
        description={`Confermi la registrazione dell'incasso ${registerRequest?.invoiceNumber || ''}?`}
        confirmLabel="Registra incasso"
        cancelLabel="Annulla"
        onCancel={() => setRegisterRequest(null)}
        onConfirm={async () => {
          await onRegisterPayment?.(registerRequest.id);
          setRegisterRequest(null);
        }}
      />
    </>
  );
}
